import React, { useMemo } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { clsx } from 'clsx';
import { format } from 'date-fns';
import { Badge } from '../ui/Badge';
import { AnimatePresence, motion } from 'framer-motion';

/**
 * Maps an event type string to a color theme and icon.
 * Color coding per spec:
 *   green  = completion events
 *   blue   = progress events
 *   amber  = escalation events
 *   red    = overdue / breach events
 *   slate  = administrative events
 */
const getEventStyle = (eventType) => {
  switch (eventType) {
    // Completion events
    case 'completed':
    case 'directive_completed':
    case 'assignment_completed':
    case 'evidence_approved':
    case 'resolved':
      return {
        icon: FiIcons.FiCheckCircle,
        color: 'green',
        bg: 'bg-green-500',
        ring: 'ring-green-200',
        badgeBg: 'bg-green-50',
        badgeText: 'text-green-700',
        badgeBorder: 'border-green-200',
        cardBorder: 'border-green-200',
        dotBg: 'bg-green-500',
      };

    // Progress events
    case 'progress_update':
    case 'directive_created':
    case 'assignment_created':
    case 'status_change':
    case 'milestone_reached':
    case 'evidence_submitted':
    case 'in_progress':
      return {
        icon: FiIcons.FiTrendingUp,
        color: 'blue',
        bg: 'bg-blue-500',
        ring: 'ring-blue-200',
        badgeBg: 'bg-blue-50',
        badgeText: 'text-blue-700',
        badgeBorder: 'border-blue-200',
        cardBorder: 'border-blue-200',
        dotBg: 'bg-blue-500',
      };

    // Escalation events
    case 'escalated':
    case 'escalation':
    case 'warning_issued':
    case 'deadline_extended':
    case 'reassigned':
      return {
        icon: FiIcons.FiAlertTriangle,
        color: 'amber',
        bg: 'bg-amber-500',
        ring: 'ring-amber-200',
        badgeBg: 'bg-amber-50',
        badgeText: 'text-amber-700',
        badgeBorder: 'border-amber-200',
        cardBorder: 'border-amber-200',
        dotBg: 'bg-amber-500',
      };

    // Overdue / breach events
    case 'overdue':
    case 'breach':
    case 'deadline_missed':
    case 'non_compliance':
    case 'blocked':
      return {
        icon: FiIcons.FiAlertOctagon,
        color: 'red',
        bg: 'bg-red-500',
        ring: 'ring-red-200',
        badgeBg: 'bg-red-50',
        badgeText: 'text-red-700',
        badgeBorder: 'border-red-200',
        cardBorder: 'border-red-200',
        dotBg: 'bg-red-500',
      };

    // Administrative events (default)
    case 'created':
    case 'approved':
    case 'submitted':
    case 'noted':
    case 'comment':
    case 'attachment':
    default:
      return {
        icon: FiIcons.FiFileText,
        color: 'slate',
        bg: 'bg-slate-400',
        ring: 'ring-slate-200',
        badgeBg: 'bg-slate-50',
        badgeText: 'text-slate-700',
        badgeBorder: 'border-slate-200',
        cardBorder: 'border-slate-200',
        dotBg: 'bg-slate-400',
      };
  }
};

/**
 * Returns a human-readable label and icon for the event source.
 */
const getSourceInfo = (source) => {
  switch (source) {
    case 'audit':
      return { label: 'Decision', icon: FiIcons.FiBookOpen };
    case 'directive':
      return { label: 'Directive', icon: FiIcons.FiGitBranch };
    case 'assignment':
      return { label: 'Assignment', icon: FiIcons.FiUser };
    default:
      return { label: 'Event', icon: FiIcons.FiCircle };
  }
};

/**
 * Formats a raw event type string into a readable label.
 * e.g. "directive_completed" -> "Directive Completed"
 */
const formatEventType = (type) => {
  if (!type) return 'Event';
  return type
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (l) => l.toUpperCase());
};

/**
 * DecisionTimeline
 *
 * Vertical lifecycle timeline showing all events from decision submission
 * through implementation. Merges audit trail, directive events, and assignment
 * events into a single chronological view sorted by date descending.
 *
 * Follows the existing EscalationTimeline.jsx pattern.
 *
 * @param {Object} props
 * @param {Array} props.auditTrail - From decision.auditTrail
 * @param {Array} props.directiveEvents - Computed from directive status changes
 * @param {Array} props.assignmentEvents - Computed from assignment completions
 */
export const DecisionTimeline = ({
  auditTrail = [],
  directiveEvents = [],
  assignmentEvents = [],
}) => {
  /** Merge all event arrays, tag each with a source, and sort descending by date */
  const mergedEvents = useMemo(() => {
    const tagged = [
      ...auditTrail.map((e) => ({ ...e, _source: 'audit' })),
      ...directiveEvents.map((e) => ({ ...e, _source: 'directive' })),
      ...assignmentEvents.map((e) => ({ ...e, _source: 'assignment' })),
    ];

    // Sort descending by date (most recent first)
    tagged.sort((a, b) => {
      const dateA = new Date(a.date || a.timestamp);
      const dateB = new Date(b.date || b.timestamp);
      return dateB - dateA;
    });

    return tagged;
  }, [auditTrail, directiveEvents, assignmentEvents]);

  // Empty state
  if (mergedEvents.length === 0) {
    return (
      <div className="text-center py-8 text-slate-400">
        <SafeIcon icon={FiIcons.FiInbox} className="text-3xl mb-2 mx-auto" />
        <p className="text-sm">No timeline events recorded</p>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Vertical timeline line */}
      <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-slate-200" />

      {/* Timeline items */}
      <AnimatePresence initial={false}>
        <div className="space-y-6">
          {mergedEvents.map((event, idx) => {
            const eventType = event.type || event.eventType || 'created';
            const style = getEventStyle(eventType);
            const sourceInfo = getSourceInfo(event._source);
            const eventDate = event.date || event.timestamp;

            return (
              <motion.div
                key={event.id || `${event._source}-${idx}`}
                initial={{ opacity: 0, x: -12 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -12 }}
                transition={{ duration: 0.25, delay: idx * 0.04 }}
                className="relative pl-14"
              >
                {/* Circular status indicator on the timeline line */}
                <div
                  className={clsx(
                    'absolute left-3.5 top-1 h-5 w-5 rounded-full border-2 border-white flex items-center justify-center ring-2',
                    style.dotBg,
                    style.ring
                  )}
                >
                  <SafeIcon
                    icon={style.icon}
                    className="text-white text-xs"
                  />
                </div>

                {/* Event content card */}
                <div
                  className={clsx(
                    'bg-white border rounded-lg p-4 hover:shadow-md transition-shadow',
                    style.cardBorder
                  )}
                >
                  {/* Header row: event type + source badge + date */}
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <div
                        className={clsx(
                          'p-1.5 rounded-lg',
                          style.badgeBg,
                          style.badgeText
                        )}
                      >
                        <SafeIcon icon={sourceInfo.icon} className="text-sm" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="font-bold text-slate-800 text-sm">
                            {formatEventType(eventType)}
                          </h4>
                          <span
                            className={clsx(
                              'px-2 py-0.5 rounded-full text-[10px] font-bold uppercase',
                              style.badgeBg,
                              style.badgeText
                            )}
                          >
                            {sourceInfo.label}
                          </span>
                        </div>
                        {eventDate && (
                          <p className="text-xs text-slate-500 mt-0.5">
                            {format(new Date(eventDate), 'dd MMM yyyy, HH:mm')}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Status badge for events that have one */}
                    {event.status && (
                      <Badge status={event.status} size="sm">
                        {event.status}
                      </Badge>
                    )}
                  </div>

                  {/* Actor information */}
                  {(event.actor || event.user || event.performedBy) && (
                    <div className="mb-2 flex items-center gap-2 text-xs text-slate-600">
                      <SafeIcon
                        icon={FiIcons.FiUser}
                        className="text-slate-400"
                      />
                      <span className="font-medium text-slate-700">
                        {event.actor || event.user || event.performedBy}
                      </span>
                      {(event.role || event.actorRole) && (
                        <span className="text-slate-400">
                          ({event.role || event.actorRole})
                        </span>
                      )}
                    </div>
                  )}

                  {/* Action description */}
                  {(event.action || event.description || event.message) && (
                    <p className="text-sm text-slate-700 mb-2">
                      {event.action || event.description || event.message}
                    </p>
                  )}

                  {/* Optional metadata section */}
                  {(event.metadata || event.details || event.directiveRef || event.assignmentRef) && (
                    <div className="mt-2 p-3 bg-slate-50 rounded-lg">
                      {/* Directive reference */}
                      {event.directiveRef && (
                        <div className="flex items-center gap-2 text-xs mb-1">
                          <SafeIcon
                            icon={FiIcons.FiGitBranch}
                            className="text-slate-400"
                          />
                          <span className="text-slate-500 font-medium">
                            Directive:
                          </span>
                          <span className="text-slate-700 font-semibold">
                            {event.directiveRef}
                          </span>
                        </div>
                      )}

                      {/* Assignment reference */}
                      {event.assignmentRef && (
                        <div className="flex items-center gap-2 text-xs mb-1">
                          <SafeIcon
                            icon={FiIcons.FiClipboard}
                            className="text-slate-400"
                          />
                          <span className="text-slate-500 font-medium">
                            Assignment:
                          </span>
                          <span className="text-slate-700 font-semibold">
                            {event.assignmentRef}
                          </span>
                        </div>
                      )}

                      {/* Generic metadata key-value pairs */}
                      {event.metadata &&
                        typeof event.metadata === 'object' &&
                        Object.entries(event.metadata).map(([key, value]) => (
                          <div
                            key={key}
                            className="flex items-center gap-2 text-xs mb-1 last:mb-0"
                          >
                            <span className="text-slate-500 font-medium capitalize">
                              {key.replace(/_/g, ' ')}:
                            </span>
                            <span className="text-slate-700">{String(value)}</span>
                          </div>
                        ))}

                      {/* Details string */}
                      {event.details && typeof event.details === 'string' && (
                        <p className="text-xs text-slate-600 italic">
                          "{event.details}"
                        </p>
                      )}
                    </div>
                  )}

                  {/* Notes */}
                  {event.notes && (
                    <div className="mt-2">
                      <p className="text-xs text-slate-600 italic">
                        "{event.notes}"
                      </p>
                    </div>
                  )}

                  {/* Attachments indicator */}
                  {event.attachments && event.attachments.length > 0 && (
                    <div className="mt-2 flex items-center gap-1 text-xs text-slate-500">
                      <SafeIcon
                        icon={FiIcons.FiPaperclip}
                        className="text-slate-400"
                      />
                      <span>
                        {event.attachments.length}{' '}
                        {event.attachments.length === 1
                          ? 'attachment'
                          : 'attachments'}
                      </span>
                    </div>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      </AnimatePresence>
    </div>
  );
};
